import { Injectable } from '@angular/core';
import {ClassroomMessage, MessageRecord, RecordArgs} from '@app/services/data.service';
import {WebSocketService} from '@app/modules/shared/services/web-socket.service';
import {WhiteboardEvents} from '@app/models/commands/whiteboard-commands';
import {WhiteboardService} from '@app/modules/whiteboard/services/whiteboard.service';
import {GeogebraApplet} from '@app/js-lib/geogebra/GeogebraApplet';

@Injectable({
  providedIn: 'root'
})
export class GeogebraStateUpdaterService {

  public applets: {[key: number]: GeogebraApplet} = {};

  private readonly teacherBroadcast = '/app/whiteboard/t';
  private modifiedAppletIds: Set<number> = new Set<number>();
  private scheduler: NodeJS.Timer | null = null;
  private schedulerIntervalMs: number = 100;

  constructor(
    private webSocketService: WebSocketService,
    private whiteboardService: WhiteboardService,
  ) {}

  /**
   * This method is called from geogebra service when an applet is edited
   *
   * @param appletId the edited applet id
   * */
  public onAppletEdited(appletId: number): void {
    this.addToSet(appletId);
  }

  /**
   * Method adds appletId to the set of modified applet Ids
   * If the scheduler is not running, run it
   *
   * @param appletId the id of applet that should be added to the set of modified applet Ids
   * */
  private addToSet(appletId: number): void {
    this.modifiedAppletIds.add(appletId);

    if (!this.scheduler) {
      this.runScheduler();
    }
  }

  /**
   * Method removes appletId from the set of modified applet Ids
   * If there is no appletId in the set (it's empty) after removing, stop the scheduler
   *
   * @param appletId the if applet that should be removed from the set of modified applet Ids
   * */
  private removeFromSet(appletId: number): void {
    this.modifiedAppletIds.delete(appletId);

    if (this.modifiedAppletIds.size === 0 && this.scheduler) {
      this.stopScheduler();
    }
  }

  /**
   * Method is starting the applet edition notifying scheduler.
   * */
  private runScheduler(): void {
    this.scheduler = setInterval(this.onSchedulerTick.bind(this), this.schedulerIntervalMs);
  }

  /**
   * Method is stopping the applet edition notifying scheduler.
   * */
  private stopScheduler(): void {
    clearInterval(this.scheduler);
    this.scheduler = null;
  }

  /**
   * Method will be
   *  called on every timer tick,
   *  send all applets states, that are in modified applet set
   *  and removed applet id from the set after sending the state
   * */
  private onSchedulerTick(): void {
    const modifiedAppletIdsCopy: Set<number> = new Set(this.modifiedAppletIds);

    modifiedAppletIdsCopy.forEach((appletId: number): void => {
      const xml: string = this.applets[appletId].getXML(); /* the xml to send */
      this.sendEditGGBApplet(appletId, xml);
      this.removeFromSet(appletId);
    });
  }

  private sendEditGGBApplet(appletId: number, xml: string): void {
    const data: string = JSON.stringify({appletId, xml});
    this.send(this.teacherBroadcast, {}, {
      store: true,
      broadcast: true,
      selfProcess: false,
      record: this.buildRecord(
        WhiteboardEvents.EDIT_GGB_APPLET,
        new RecordArgs(data)
      )
    });
  }

  private buildRecord(command: string, args: RecordArgs): MessageRecord {
    return this.whiteboardService.buildRecord(command, args);
  }

  private send(destination: string, headers: object, message: ClassroomMessage): void {
    this.webSocketService.send(destination, JSON.stringify(message));
  }
}
